import { t as tr } from '../i18n.js'
import { useState, type FormEvent, type ReactNode } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, useNavigate } from 'react-router'
import {
  DndContext, KeyboardSensor, MouseSensor, TouchSensor, closestCenter, useSensor, useSensors,
  type DragEndEvent,
} from '@dnd-kit/core'
import { SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { api, ApiError, browserTimezone, weekCloseExample, type Portfolio, type Project } from '../api.js'
import { atLeast, useAuth } from '../auth.js'
import { usePref } from '../prefs.js'
import { ArrowDown, ArrowUp, GripVertical } from 'lucide-react'

export function ProjectsPage() {
  const { user } = useAuth()
  const qc = useQueryClient()
  const [showArchived, setShowArchived] = usePref<boolean>('projectsShowArchived', false)
  const projects = useQuery({ queryKey: ['projects', showArchived], queryFn: () => api<{ projects: Project[] }>(`/projects?includeArchived=${showArchived}`) })
  const portfolios = useQuery({ queryKey: ['portfolios'], queryFn: () => api<{ portfolios: Portfolio[] }>('/portfolios') })
  const byPortfolio = new Map((portfolios.data?.portfolios ?? []).map((p) => [p.id, p.name]))
  const canCreate = atLeast(user, 'manager')
  const [q, setQ] = useState('')
  const [sort, setSort] = useState<{ col: string; desc: boolean }>({ col: 'priority', desc: false })
  const [hidden, setHidden] = usePref<string[]>('hiddenProjects', [])
  const [showHidden, setShowHidden] = usePref<boolean>('projectsShowHidden', false)
  const [err, setErr] = useState<string | null>(null)
  const hiddenSet = new Set(hidden)
  const rows = (projects.data?.projects ?? [])
    .filter((p) => showHidden || !hiddenSet.has(p.id))
    .filter((p) => !q || `${p.code} ${p.name}`.toLowerCase().includes(q.toLowerCase()))
    .sort((a, b) => {
      const v = (p: Project) => sort.col === 'priority' ? p.position : sort.col === 'code' ? p.code : sort.col === 'name' ? p.name : sort.col === 'status' ? p.status : sort.col === 'portfolio' ? (byPortfolio.get(p.portfolioId ?? '') ?? '') : sort.col === 'members' ? p.memberCount : sort.col === 'role' ? (p.myRole ?? '') : p.timezoneOfTruth
      const x = v(a), y = v(b); const c = typeof x === 'number' && typeof y === 'number' ? x - y : String(x).localeCompare(String(y))
      return sort.desc ? -c : c
    })
  const mainRows = rows.filter((p) => !p.isPrivate)
  const privateRows = rows.filter((p) => p.isPrivate)
  // Dragging only makes sense while the list is shown in priority order.
  const manualOrder = sort.col === 'priority' && !sort.desc && !q
  const th = (col: string, label: string, tip: string) => <th onClick={() => setSort((s) => s.col === col ? { col, desc: !s.desc } : { col, desc: false })} style={{ cursor: 'pointer', userSelect: 'none' }} data-tip={`Sort by ${tip}`}>{label}{sort.col === col ? (sort.desc ? <ArrowDown size={12} aria-hidden /> : <ArrowUp size={12} aria-hidden />) : ''}</th>
  const head = (
    <tr>{th('priority', '#', 'priority order (drag rows to change it)')}{th('code', 'Code', 'code')}{th('name', 'Name', 'name')}{th('status', 'Status', 'status')}{th('portfolio', 'Portfolio', 'portfolio')}{th('tz', 'Timezone', 'timezone of truth')}{th('members', 'Members', 'member count')}{th('role', 'Your role', 'your role')}<th></th></tr>
  )

  const move = useMutation({
    mutationFn: ({ id, afterProjectId, beforeProjectId }: { id: string; afterProjectId: string | null; beforeProjectId: string | null }) =>
      api(`/projects/${id}/move`, { method: 'POST', json: { afterProjectId, beforeProjectId } }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['projects'] }),
    onError: (e) => { setErr(e instanceof ApiError ? e.message : tr('Could not reorder projects')); void qc.invalidateQueries({ queryKey: ['projects'] }) },
  })
  // Neighbour-pair drop, like the board: the server computes the final rank;
  // the cache gets the midpoint so the row doesn't snap back meanwhile.
  const reorder = (list: Project[]) => (e: DragEndEvent) => {
    const aid = String(e.active.id), oid = String(e.over?.id ?? '')
    if (!oid || aid === oid) return
    const from = list.findIndex((p) => p.id === aid), to = list.findIndex((p) => p.id === oid)
    if (from < 0 || to < 0) return
    const order = [...list]; const [moved] = order.splice(from, 1); order.splice(to, 0, moved!)
    const i = order.findIndex((p) => p.id === aid)
    const after = order[i - 1] ?? null, before = order[i + 1] ?? null
    const rank = after && before ? (after.position + before.position) / 2 : after ? after.position + 1 : before ? before.position - 1 : moved!.position
    qc.setQueryData<{ projects: Project[] }>(['projects', showArchived], (old) =>
      old ? { projects: old.projects.map((p) => p.id === aid ? { ...p, position: rank } : p) } : old)
    setErr(null)
    move.mutate({ id: aid, afterProjectId: after?.id ?? null, beforeProjectId: before?.id ?? null })
  }
  const hideCell = (p: Project) => hiddenSet.has(p.id)
    ? <button className="link small" data-tip={tr("Show this project in your lists again")} onClick={() => setHidden(hidden.filter((h) => h !== p.id))}>Unhide</button>
    : <button className="link small" data-tip={tr("Hide this project from your lists (just for you; the project is unchanged)")} onClick={() => setHidden([...hidden, p.id])}>Hide</button>

  return (
    <>
      <div className="row between">
        <div><h1>{tr("Projects")}</h1><p className="muted">{canCreate ? tr("Projects you can see. Managers and admins see all.") : tr("Projects you are a member of.")}</p></div>
        {canCreate && <Link className="button primary" to="/projects/new" data-tip={tr("Create a project — you'll choose its permanent timezone-of-truth")}>{tr("New project")}</Link>}
      </div>
      <div className="row toolbar">
        <input placeholder={tr("Search code or name…")} value={q} onChange={(e) => setQ(e.target.value)} data-tip={tr("Filter the list by project code or name")} />
        <label className="inline"><input type="checkbox" checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} /> Show archived</label>
        <label className="inline" data-tip={tr("Hidden projects are a personal view preference; nothing about the project changes")}><input type="checkbox" checked={showHidden} onChange={(e) => setShowHidden(e.target.checked)} /> Show hidden{hidden.length ? ` (${hidden.length})` : ''}</label>
      </div>
      {err && <p className="bad">{err}</p>}
      <section className="card">
        {mainRows.length === 0 && <p className="muted">{projects.data?.projects.length ? tr("No projects match.") : tr("No projects yet.")}</p>}
        {mainRows.length > 0 && (
          <SortableTable rows={mainRows} head={head} draggable={manualOrder && atLeast(user, 'manager')} onReorder={reorder(mainRows)}>
            {(p, grip) => (
              <>
                <td className="small muted">{grip}</td>
                <td><Link to={`/projects/${p.id}`} data-tip={tr("Open the project")}><strong>{p.code}</strong></Link></td>
                <td><Link to={`/projects/${p.id}`}>{p.name}</Link>{hiddenSet.has(p.id) && <span className="tag">{tr("hidden")}</span>}</td>
                <td>{p.status.replace('_', ' ')}</td>
                <td>{p.portfolioId ? byPortfolio.get(p.portfolioId) ?? '—' : '—'}</td>
                <td className="small">{p.timezoneOfTruth}</td>
                <td>{p.memberCount}</td>
                <td>{p.myRole ?? <span className="muted">—</span>}</td>
                <td>{hideCell(p)}</td>
              </>
            )}
          </SortableTable>
        )}
      </section>
      {privateRows.length > 0 && (
        <section className="card">
          <h2>{tr("My private projects")}</h2>
          <p className="muted small">{tr("Only you can see these. Drag to prioritize.")}</p>
          <SortableTable rows={privateRows} head={head} draggable={manualOrder} onReorder={reorder(privateRows)}>
            {(p, grip) => (
              <>
                <td className="small muted">{grip}</td>
                <td><Link to={`/projects/${p.id}`} data-tip={tr("Open the project")}><strong>{p.code}</strong></Link></td>
                <td><Link to={`/projects/${p.id}`}>{p.name}</Link><span className="tag">{tr("private")}</span>{hiddenSet.has(p.id) && <span className="tag">{tr("hidden")}</span>}</td>
                <td>{p.status.replace('_', ' ')}</td>
                <td>{p.portfolioId ? byPortfolio.get(p.portfolioId) ?? '—' : '—'}</td>
                <td className="small">{p.timezoneOfTruth}</td>
                <td>{p.memberCount}</td>
                <td>{p.myRole ?? <span className="muted">—</span>}</td>
                <td>{hideCell(p)}</td>
              </>
            )}
          </SortableTable>
        </section>
      )}
      <section className="card stack">
        <h2>{tr("Portfolios")}</h2>
        <ul className="help small muted">
          <li>{tr("A portfolio groups related projects under one initiative (a client, a programme, a department).")}</li>
          <li>{tr("Create one from the Portfolios page, then file projects under it from a project's")} <strong>{tr("Settings")}</strong> (or when creating the project). Reports can group by portfolio.</li>
          <li>{tr("Portfolios don't affect approvals — they add a shared board, list and Gantt, and cross-project links.")}</li>
        </ul>
        <p><Link to="/portfolios" data-tip={tr("All portfolios you can see; create one there")}>{tr("Portfolios →")}</Link></p>
      </section>
    </>
  )
}

function SortableTable({ rows, head, draggable, onReorder, children }: {
  rows: Project[]; head: ReactNode; draggable: boolean; onReorder: (e: DragEndEvent) => void
  children: (p: Project, grip: ReactNode) => ReactNode
}) {
  const sensors = useSensors(
    // Mouse: drag after 4px. Touch: long-press (250ms) so a swipe still scrolls.
    useSensor(MouseSensor, { activationConstraint: { distance: 4 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 250, tolerance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  )
  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onReorder}>
      <table className="table">
        <thead>{head}</thead>
        <SortableContext items={rows.map((p) => p.id)} strategy={verticalListSortingStrategy}>
          <tbody>
            {rows.map((p) => <SortableRow key={p.id} p={p} draggable={draggable}>{children}</SortableRow>)}
          </tbody>
        </SortableContext>
      </table>
    </DndContext>
  )
}

function SortableRow({ p, draggable, children }: { p: Project; draggable: boolean; children: (p: Project, grip: ReactNode) => ReactNode }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: p.id, disabled: !draggable })
  const grip = draggable
    ? <span className="grip rowgrip" {...attributes} {...listeners} aria-roledescription="draggable project" data-tip={tr("Drag to change priority; Space + arrows works from the keyboard")}><GripVertical size={14} /></span>
    : <span className="grip" aria-hidden><GripVertical size={14} opacity={0.25} /></span>
  return (
    <tr ref={setNodeRef} style={{ transform: CSS.Translate.toString(transform), transition, opacity: isDragging ? 0.5 : 1 }} className={p.archivedAt ? 'muted' : ''}>
      {children(p, grip)}
    </tr>
  )
}

export function NewProjectPage() {
  const nav = useNavigate()
  const portfolios = useQuery({ queryKey: ['portfolios'], queryFn: () => api<{ portfolios: Portfolio[] }>('/portfolios') })
  const [code, setCode] = useState('')
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [tz, setTz] = useState(browserTimezone())
  const [confirm, setConfirm] = useState(false)
  const [portfolioId, setPortfolioId] = useState('')
  const [startsOn, setStartsOn] = useState('')
  const [endsOn, setEndsOn] = useState('')
  const [isPrivate, setIsPrivate] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  const example = weekCloseExample(tz)

  const create = useMutation({
    mutationFn: () => api<{ project: Project }>('/projects', { method: 'POST', json: {
      code, name, description: description || null, timezoneOfTruth: tz, confirmTimezone: true,
      portfolioId: portfolioId || null, startsOn: startsOn || null, endsOn: endsOn || null, isPrivate,
    } }),
    onSuccess: (r) => nav(`/projects/${r.project.id}`),
    onError: (e) => setErr(e instanceof ApiError ? e.message : 'Could not create project'),
  })
  const submit = (e: FormEvent) => { e.preventDefault(); setErr(null); create.mutate() }

  return (
    <>
      <h1>{tr("New project")}</h1>
      <p className="muted">{tr("Choose the timezone carefully — it cannot be changed afterwards.")}</p>
      <form className="card stack" onSubmit={submit}>
        <div className="row">
          <label style={{ flex: '0 0 10rem' }}>{tr("Code")}<input value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} required maxLength={20} pattern="[A-Za-z0-9][A-Za-z0-9_-]*" placeholder="DEN" /></label>
          <label style={{ flex: 1 }}>{tr("Name")}<input value={name} onChange={(e) => setName(e.target.value)} required maxLength={200} /></label>
        </div>
        <label>{tr("Description")}<textarea rows={3} value={description} onChange={(e) => setDescription(e.target.value)} maxLength={5000} /></label>
        <div className="row">
          <label>{tr("Starts")}<input type="date" value={startsOn} onChange={(e) => setStartsOn(e.target.value)} /></label>
          <label>{tr("Ends")}<input type="date" value={endsOn} onChange={(e) => setEndsOn(e.target.value)} /></label>
          <label style={{ flex: 1 }}>{tr("Portfolio")}
            <select value={portfolioId} onChange={(e) => setPortfolioId(e.target.value)}>
              <option value="">—</option>
              {portfolios.data?.portfolios.filter((p) => !p.archivedAt).map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </label>
        </div>
        <label className="inline" data-tip={tr("A private project appears only in your own project list — no one else can see it, whatever their role")}>
          <input type="checkbox" checked={isPrivate} onChange={(e) => setIsPrivate(e.target.checked)} /> {tr("Private — only you will see this project")}
        </label>

        <fieldset className="tzbox stack">
          <legend>{tr("Timezone of truth")}</legend>
          <label>IANA zone<input value={tz} onChange={(e) => { setTz(e.target.value); setConfirm(false) }} required placeholder="America/Denver" list="tz-suggestions" />
            <datalist id="tz-suggestions">{COMMON_ZONES.map((z) => <option key={z} value={z} />)}</datalist>
          </label>
          <p className="small">
            Every day and week boundary on this project — cutoffs, approvals, weekly totals — is measured in this zone, no matter where each person is.
            {example.ok
              ? <> For example, the next week will close at <strong>{example.text}</strong>.</>
              : <> <span className="bad">{tr("That does not look like a valid zone.")}</span></>}
          </p>
          <label className="inline"><input type="checkbox" checked={confirm} onChange={(e) => setConfirm(e.target.checked)} required disabled={!example.ok} />
            I understand this zone is permanent for this project. To change it later I would have to create a new project.</label>
        </fieldset>

        {err && <p className="bad">{err}</p>}
        <div className="row"><button className="button primary" disabled={!confirm || create.isPending}>{tr("Create project")}</button><Link className="button" to="/projects">{tr("Cancel")}</Link></div>
      </form>
    </>
  )
}

/** The zones offered in every `tz-suggestions` datalist (project creation, People). */
export const COMMON_ZONES = [
  'America/Los_Angeles', 'America/Denver', 'America/Chicago', 'America/New_York', 'America/Toronto', 'America/Sao_Paulo',
  'Europe/London', 'Europe/Paris', 'Europe/Berlin', 'Europe/Madrid', 'Europe/Kyiv', 'Asia/Dubai', 'Asia/Kolkata',
  'Asia/Singapore', 'Asia/Shanghai', 'Asia/Tokyo', 'Asia/Seoul', 'Australia/Sydney', 'Pacific/Auckland', 'UTC',
]
